import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { MotionConfig, motion } from 'framer-motion'
import { ArrowDown, CalendarHeart, Check, MapPin, PartyPopper, Plus, Shirt, Smartphone, X, type LucideIcon } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { HeroVideo } from '@/components/home/HeroVideo'
import { Sticker, type StickerName } from '@/components/ui/Sticker'
import { registerClick } from '@/lib/burst'
import { fireConfetti, fireConfettiAt } from '@/lib/confetti'
import { cn } from '@/lib/utils'

type Detail = { term: string; detail: string }
type Step = { title: string; text: string; year: string }
type Game = { name: string; kicker: string; text: string; rules: string[]; cta: string }
type ProgramStep = { title: string; text: string }
type Faq = { q: string; a: string }

/** Pulsante principale (corallo) e secondario (bianco): pillole tonde, unico vocabolario dei pulsanti della home. */
const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded-full bg-coral px-7 py-4 font-display text-lg font-semibold text-white shadow-[0_14px_30px_-12px_var(--color-coral)] transition-[transform,background-color] duration-200 ease-out-expo hover:-translate-y-0.5 hover:-rotate-1 hover:bg-coral-ink active:translate-y-0 active:scale-95'
const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded-full bg-paper px-7 py-4 font-display text-lg font-semibold text-ink shadow-card transition-transform duration-200 ease-out-expo hover:-translate-y-0.5 hover:rotate-1 active:translate-y-0 active:scale-95'

/** Lo sticker "salta dentro" quando entra nello schermo (con "riduci movimento" compare e basta). */
const popIn = {
  initial: { opacity: 0, scale: 0.6, rotate: -10 },
  whileInView: { opacity: 1, scale: 1, rotate: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { type: 'spring', stiffness: 220, damping: 14 },
} as const

/** Card che sale piano quando entra nello schermo; `index` sfasa le card vicine. */
const riseIn = (index = 0) =>
  ({
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-60px' },
    transition: { duration: 0.55, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] },
  }) as const

/** Scorrimento morbido solo qui (non globale: gli scroll programmatici del resto del sito restano immediati). */
function scrollToParty(event: MouseEvent<HTMLAnchorElement>) {
  const target = document.getElementById('festa')
  // Ctrl/Cmd/Shift-click e tasto centrale restano al browser.
  if (!target || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView({ behavior: isReduced ? 'auto' : 'smooth' })
  // Come la navigazione al frammento: il focus passa alla sezione, il Tab successivo continua da lì.
  target.focus({ preventScroll: true })
}

/** Stellina a quattro punte, separatore della striscia scorrevole. */
function Sparkle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M12 1c1 6 4 9 11 11c-7 2-10 5-11 11c-1-6-4-9-11-11c7-2 10-5 11-11z" fill="currentColor" />
    </svg>
  )
}

function Hero() {
  const { t } = useTranslation()
  // Coriandoli una volta all'arrivo sulla pagina.
  useEffect(() => {
    const id = window.setTimeout(fireConfetti, 600)
    return () => window.clearTimeout(id)
  }, [])
  return (
    <section aria-labelledby="hero-heading" className="night relative isolate flex min-h-svh flex-col justify-end overflow-hidden px-5 pt-28 pb-12 sm:px-8 sm:pb-16 lg:px-12 short:pt-20 short:pb-4">
      {/* Sotto lg la foto di Zambe è lo sfondo dell'hero (dietro l'eventuale video), velata dal basso per il testo. */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 -z-20 h-[62svh] lg:hidden">
        <img src="/media/zambe-ippodromo.webp" alt="" width={928} height={1152} fetchPriority="high" className="size-full object-cover object-[center_42%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
      </div>
      <HeroVideo />
      <div className="mx-auto flex w-full max-w-7xl flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:items-end lg:gap-12">
        <div className="motion-safe:animate-reveal">
          <Sticker name="cap-hero" priority onDark className="-ml-3 size-28 max-lg:hidden sm:size-36 short:hidden" />
          {/* L'etichetta "Zambe laureato · data" sta dentro l'h1: la parola chiave del sito è nel titolo principale. */}
          <h1 id="hero-heading" className="mt-2 short:mt-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-paper/15 px-4 py-2 font-body text-sm font-bold text-paper ring-1 ring-paper/25 backdrop-blur-sm sm:text-base">
              <span aria-hidden="true" className="size-2 rounded-full bg-coral motion-safe:animate-pulse" />
              {t('home.hero.date')}
            </span>
            <span className="hero-title mt-5 block max-w-[12ch] text-hero text-balance text-paper drop-shadow-[0_4px_24px_rgb(0_0_0/0.25)] short:mt-2 short:max-w-none short:text-[clamp(2.5rem,13svh,4.5rem)]">
              {t('home.hero.title')}
            </span>
          </h1>
          <p className="mt-6 max-w-[46ch] text-lg leading-relaxed font-semibold text-paper/90 sm:text-xl short:mt-2 short:text-base">{t('home.hero.lead')}</p>
          <div className="mt-9 flex flex-wrap items-center gap-4 short:mt-4">
            <LocalizedLink to="zambePong" className={primaryButton}>
              {t('home.hero.cta')}
            </LocalizedLink>
            <a href="#festa" onClick={scrollToParty} className={secondaryButton}>
              {t('home.hero.scroll')}
              <ArrowDown aria-hidden="true" className="size-5" />
            </a>
          </div>
        </div>
        {/* Zambe vincente all'ippodromo, da lg nella colonna di destra (sotto lg è lo sfondo, sopra). */}
        <motion.img
          src="/media/zambe-ippodromo.webp"
          alt={t('home.hero.photoAlt')}
          width={928}
          height={1152}
          fetchPriority="high"
          initial={{ opacity: 0, y: 70, rotate: 10 }}
          animate={{ opacity: 1, y: 0, rotate: 4 }}
          transition={{ type: 'spring', stiffness: 110, damping: 14, delay: 0.3 }}
          className="mb-6 hidden w-full self-start rounded-3xl shadow-lift ring-8 ring-paper lg:block short:hidden"
        />
      </div>
    </section>
  )
}

/** Striscia corallo che scorre sotto l'hero: il contenuto è doppio, così il giro ricomincia senza salti. */
function Marquee() {
  const { t } = useTranslation()
  const items = t('home.marquee', { returnObjects: true }) as string[]
  const run = (
    <span className="flex shrink-0 items-center gap-8 pr-8">
      {items.map((item) => (
        <span key={item} className="flex items-center gap-8">
          <span>{item}</span>
          <Sparkle className="size-4 text-white/70" />
        </span>
      ))}
    </span>
  )
  return (
    <div aria-hidden="true" className="overflow-hidden bg-coral py-4 font-display text-2xl font-semibold whitespace-nowrap text-white sm:text-3xl">
      <div className="flex w-max motion-safe:animate-marquee">
        {run}
        {run}
      </div>
    </div>
  )
}

const DETAIL_ICONS: LucideIcon[] = [CalendarHeart, MapPin, Shirt, Smartphone]

function Party() {
  const { t } = useTranslation()
  const details = t('home.party.details', { returnObjects: true }) as Detail[]
  return (
    <section id="festa" tabIndex={-1} aria-labelledby="party-heading" className="scroll-mt-16 overflow-hidden px-5 py-section outline-none sm:px-8 md:scroll-mt-20 lg:px-12">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
        <div>
          <p className="inscription text-xl text-coral-ink">{t('home.party.kicker')}</p>
          <h2 id="party-heading" className="hero-title mt-2 text-h2 text-ink text-balance">{t('home.party.title')}</h2>
          <p className="mt-6 max-w-[52ch] text-lg leading-relaxed text-ink-soft sm:text-xl">{t('home.party.lead')}</p>
          <dl className="mt-10 grid gap-4 sm:grid-cols-2">
            {details.map((item, i) => {
              const Icon = DETAIL_ICONS[i % DETAIL_ICONS.length]
              return (
                <motion.div key={item.term} {...riseIn(i)} className="flex items-start gap-4 rounded-3xl border border-line bg-paper p-5 shadow-card">
                  <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-coral/10 text-coral-ink">
                    <Icon aria-hidden="true" className="size-5" />
                  </span>
                  <div>
                    <dt className="font-display text-lg font-semibold text-ink">{item.term}</dt>
                    <dd className="mt-0.5 text-base leading-snug text-ink">{item.detail}</dd>
                  </div>
                </motion.div>
              )
            })}
          </dl>
        </div>
        <div className="relative mx-auto w-full max-w-md">
          <motion.div {...popIn}>
            <Sticker name="toast" float rotate={-4} className="w-full" />
          </motion.div>
          <motion.div {...popIn} transition={{ ...popIn.transition, delay: 0.15 }} className="absolute -right-4 -bottom-6 w-40 sm:w-48">
            <Sticker name="balloons" float delay={2} rotate={8} className="w-full" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}


/** Il percorso di Zambe in quattro tappe numerate, in ordine. */
function Journey() {
  const { t } = useTranslation()
  const steps = t('home.journey.steps', { returnObjects: true }) as Step[]
  return (
    <section aria-labelledby="journey-heading" className="relative overflow-hidden px-5 py-section sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <motion.div {...popIn} className="mx-auto w-40 sm:w-48">
            <Sticker name="diploma" float rotate={-6} className="w-full" />
          </motion.div>
          <p className="inscription mt-2 text-xl text-coral-ink">{t('home.journey.kicker')}</p>
          <h2 id="journey-heading" className="hero-title mt-2 text-h2 text-ink text-balance">{t('home.journey.title')}</h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-soft sm:text-xl">{t('home.journey.lead')}</p>
        </div>
        <ol className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <motion.li key={step.title} {...riseIn(i)} className="flex flex-col rounded-3xl border border-line bg-paper p-6 shadow-card transition-transform duration-300 ease-out-expo hover:-translate-y-1.5 hover:-rotate-1">
              <div className="flex items-center justify-between">
                <span className="grid size-12 place-items-center rounded-2xl bg-coral font-display text-2xl font-bold text-white">
                  <span className="sr-only">{t('home.journey.stepLabel')} </span>
                  {i + 1}
                </span>
                <span className="rounded-full bg-paper-deep px-3 py-1 text-sm font-bold text-ink-soft">{step.year}</span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold text-ink">{step.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-ink-soft">{step.text}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function GameCard({ game, sticker, tone, index, children }: { game: Game; sticker: StickerName; tone: string; index: number; children: ReactNode }) {
  return (
    <motion.article
      {...riseIn(index)}
      whileHover={{ y: -6, rotate: index % 2 === 0 ? -0.8 : 0.8 }}
      className={cn('relative flex flex-col overflow-hidden rounded-[2rem] p-7 pt-36 sm:p-10 sm:pt-10', tone)}
    >
      <Sticker name={sticker} float delay={index} rotate={index % 2 === 0 ? 8 : -8} className="absolute -top-2 right-2 w-40 sm:-top-4 sm:-right-2 sm:w-52" />
      <p className="inscription text-xl text-coral-ink">{game.kicker}</p>
      <h3 className="hero-title mt-1 text-[clamp(2.75rem,2rem+3vw,4.5rem)] text-ink sm:max-w-[60%]">{game.name}</h3>
      <p className="mt-5 max-w-[46ch] text-lg leading-relaxed text-ink sm:max-w-[62%]">{game.text}</p>
      <ul className="mt-6 space-y-3">
        {game.rules.map((rule) => (
          <li key={rule} className="flex gap-3 text-base leading-snug text-ink">
            <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-coral text-white">
              <Check aria-hidden="true" className="size-3.5" strokeWidth={3} />
            </span>
            {rule}
          </li>
        ))}
      </ul>
      <div className="mt-8">{children}</div>
    </motion.article>
  )
}

function Games() {
  const { t } = useTranslation()
  const pong = t('home.games.pong', { returnObjects: true }) as Game
  const bet = t('home.games.bet', { returnObjects: true }) as Game
  return (
    <section aria-labelledby="games-heading" className="bg-paper-deep px-5 py-section sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="inscription text-xl text-coral-ink">{t('home.games.kicker')}</p>
          <h2 id="games-heading" className="hero-title mt-2 text-h2 text-ink text-balance">{t('home.games.title')}</h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-soft sm:text-xl">{t('home.games.lead')}</p>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <GameCard game={pong} sticker="beer-pong" tone="bg-paper shadow-card" index={0}>
            <LocalizedLink to="zambePong" className={primaryButton}>{pong.cta}</LocalizedLink>
          </GameCard>
          <GameCard game={bet} sticker="coins" tone="bg-paper shadow-card" index={1}>
            <LocalizedLink to="zambePong" className={secondaryButton}>{bet.cta}</LocalizedLink>
          </GameCard>
        </div>
      </div>
    </section>
  )
}

/** I momenti della serata, in ordine, senza orari: numeri corallo uniti da una linea. */
function Program() {
  const { t } = useTranslation()
  const steps = t('home.program.steps', { returnObjects: true }) as ProgramStep[]
  return (
    <section aria-labelledby="program-heading" className="relative overflow-hidden bg-paper-deep px-5 py-section sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-end justify-between gap-8">
          <div className="max-w-2xl">
            <p className="inscription text-xl text-coral-ink">{t('home.program.kicker')}</p>
            <h2 id="program-heading" className="hero-title mt-2 text-h2 text-ink text-balance">{t('home.program.title')}</h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft sm:text-xl">{t('home.program.lead')}</p>
          </div>
          <motion.div {...popIn} className="hidden w-40 shrink-0 md:block lg:w-48">
            <Sticker name="party-hat" float rotate={10} className="w-full" />
          </motion.div>
        </div>
        <ol className="relative mt-14 grid gap-8 md:grid-cols-5 md:gap-6">
          <span aria-hidden="true" className="absolute top-6 right-[10%] left-[10%] hidden h-0.5 bg-coral/30 md:block" />
          {steps.map((step, i) => (
            <motion.li key={step.title} {...riseIn(i)} className="relative flex gap-4 md:flex-col md:items-center md:text-center">
              <span className="grid size-12 shrink-0 place-items-center rounded-full bg-coral font-display text-xl font-bold text-white ring-8 ring-paper-deep">
                {i + 1}
              </span>
              <div>
                <h3 className="font-display text-xl font-semibold text-ink md:mt-4">{step.title}</h3>
                <p className="mt-1.5 text-base leading-relaxed text-ink-soft">{step.text}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/** Domande frequenti su torneo e monete: fisarmonica nativa (`details`), apribile anche da tastiera. */
function Faqs() {
  const { t } = useTranslation()
  const items = t('home.faq.items', { returnObjects: true }) as Faq[]
  return (
    <section aria-labelledby="faq-heading" className="px-5 py-section sm:px-8 lg:px-12">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="inscription text-xl text-coral-ink">{t('home.faq.kicker')}</p>
          <h2 id="faq-heading" className="hero-title mt-2 text-h2 text-ink text-balance">{t('home.faq.title')}</h2>
          <motion.div {...popIn} className="mt-8 hidden w-56 lg:block">
            <Sticker name="mascot" float rotate={-6} className="w-full" />
          </motion.div>
        </div>
        <div className="space-y-3">
          {items.map((item, i) => (
            <motion.details key={item.q} {...riseIn(i)} className="group rounded-3xl border border-line bg-paper px-6 shadow-card open:pb-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 font-display text-lg font-semibold text-ink marker:hidden [&::-webkit-details-marker]:hidden">
                {item.q}
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-coral/10 text-coral-ink transition-transform duration-300 ease-out-expo group-open:rotate-45">
                  <Plus aria-hidden="true" className="size-4" strokeWidth={3} />
                </span>
              </summary>
              <p className="max-w-[60ch] text-base leading-relaxed text-ink-soft">{item.a}</p>
            </motion.details>
          ))}
        </div>
      </div>
    </section>
  )
}

/** Cinque lanci di coriandoli in tre secondi aprono la sorpresa. */
const SURPRISE_CLICKS = 5
const SURPRISE_WINDOW_MS = 3_000

/** Solo la foto, chiudibile con la X, con Esc o toccando fuori. */
function Surprise({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => ref.current?.showModal(), [])
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(event) => event.target === event.currentTarget && ref.current?.close()}
      className="m-auto overflow-visible bg-transparent p-0 backdrop:bg-ink/75 backdrop:backdrop-blur-sm"
    >
      <img src="/media/curva.webp" alt="" width={720} height={1166} className="block h-auto max-h-[88dvh] w-auto max-w-[92vw] rounded-3xl shadow-lift" />
      <button
        type="button"
        onClick={() => ref.current?.close()}
        aria-label={t('aria.close')}
        className="absolute top-3 right-3 grid size-11 place-items-center rounded-full bg-paper text-ink shadow-lift transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
      >
        <X className="size-5" aria-hidden="true" />
      </button>
    </dialog>
  )
}

function Finale() {
  const { t } = useTranslation()
  const clicks = useRef<number[]>([])
  const [isSurpriseOpen, setSurpriseOpen] = useState(false)

  function celebrate(event: MouseEvent<HTMLButtonElement>) {
    fireConfettiAt(event.clientX, event.clientY)
    const next = registerClick(clicks.current, performance.now(), SURPRISE_CLICKS, SURPRISE_WINDOW_MS)
    clicks.current = next.clicks
    if (next.isBurst) setSurpriseOpen(true)
  }

  return (
    <section aria-labelledby="finale-heading" className="night relative overflow-hidden px-5 py-section text-center sm:px-8 lg:px-12">
      <Sticker name="balloons" float rotate={-10} onDark className="absolute top-10 -left-10 hidden w-56 md:block" />
      <Sticker name="party-hat" float delay={1.5} rotate={14} onDark className="absolute right-[6%] bottom-10 hidden w-40 md:block" />
      <motion.div {...popIn} className="mx-auto w-56 sm:w-72">
        <Sticker name="cap-toss" float onDark className="w-full" />
      </motion.div>
      <h2 id="finale-heading" className="hero-title mt-4 text-[clamp(3rem,2rem+6vw,7rem)] text-paper text-balance">{t('home.finale.title')}</h2>
      <p className="mx-auto mt-6 max-w-[48ch] text-lg leading-relaxed text-paper/85 sm:text-xl">{t('home.finale.lead')}</p>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <LocalizedLink to="zambePong" className={primaryButton}>{t('home.finale.cta')}</LocalizedLink>
        <button type="button" onClick={celebrate} className={secondaryButton}>
          <PartyPopper aria-hidden="true" className="size-5 text-coral" />
          {t('home.finale.confetti')}
        </button>
      </div>
      {isSurpriseOpen && <Surprise onClose={() => setSurpriseOpen(false)} />}
    </section>
  )
}

/** Landing della festa: video e coriandoli, l'invito, la serata, il percorso di Zambe, i giochi, le domande frequenti e il gran finale. */
export function Home() {
  return (
    <MotionConfig reducedMotion="user">
      <main id="main">
        <Hero />
        <Marquee />
        <Party />
        <Program />
        <Journey />
        <Games />
        <Faqs />
        <Finale />
      </main>
    </MotionConfig>
  )
}
